import styled, { css } from 'styled-components';
import colors from '../../../helpers/colors';
import fonts from '../../../helpers/fonts';
import mediaQuery from '../../../helpers/mediaQuery';
import spacing from '../../../helpers/spacing';
import Image from '../../Image';

const LARGE_DESKTOP_BREAKPOINT = '1880px';

/* large desktop sizes (>1880px) */
const TRAPEZOID_WIDTH_LARGE = '842px';
const BIG_TRIANGLE_WIDTH_LARGE = '192px';

/* smaller desktop sizes */
const BANNER_HEIGHT = '408px';
const TRAPEZOID_WIDTH = '43vw';
const BIG_TRIANGLE_WIDTH = '17vw';
const SMALL_TRIANGLE_HEIGHT = '102px';
const SMALL_TRIANGLE_WIDTH = '192px';

/* tablet sizes */
const BANNER_WIDTH_TABLET = '100vw';
const TRAPEZOID_HEIGHT_TABLET = '176px';
const BIG_TRIANGLE_HEIGHT_TABLET = '180px';
const SMALL_TRIANGLE_HEIGHT_TABLET = '315px';
const SMALL_TRIANGLE_WIDTH_TABLET = '100vw';
const TOTAL_HEIGHT_TABLET = '591px';
const IMAGE_OFFSET_TABLET = '211px';

/* mobile sizes */
const BANNER_WIDTH_MOBILE = '100vw';
const TRAPEZOID_HEIGHT_MOBILE = '244px';
const BIG_TRIANGLE_HEIGHT_MOBILE = '92px';
const SMALL_TRIANGLE_HEIGHT_MOBILE = '190px';
const SMALL_TRIANGLE_WIDTH_MOBILE = '100vw';
const TOTAL_HEIGHT_MOBILE = '490px';
const IMAGE_OFFSET_MOBILE = '250px';

export const ImageWrapper = styled.div`
  position: absolute;
  width: 100vw;
  top: ${IMAGE_OFFSET_MOBILE};
  right: 0;
  z-index: -3;

  ${mediaQuery.tabletPortrait`
    top: ${IMAGE_OFFSET_TABLET};
  `}

  ${mediaQuery.tabletLandscape`
    top: 0;
    width: calc(100vw - ${TRAPEZOID_WIDTH});
  `}

  @media screen and (min-width: ${LARGE_DESKTOP_BREAKPOINT}) {
    width: calc(1880px - ${TRAPEZOID_WIDTH_LARGE});
  }
`;

export const MainImage = styled(Image)`
  object-fit: cover;
  height: calc(${TOTAL_HEIGHT_MOBILE} - ${IMAGE_OFFSET_MOBILE});

  ${mediaQuery.tabletPortrait`
    height: calc(${TOTAL_HEIGHT_TABLET} - ${IMAGE_OFFSET_TABLET});
  `}

  ${mediaQuery.tabletLandscape`
    height: ${BANNER_HEIGHT};
  `}
`;

export const Heading = styled.h1`
  ${fonts.headingXLarge}

  margin: ${spacing(4)} 0 0;
  ${mediaQuery.tabletPortrait`
    margin: ${spacing(6)} 0 0;
  `}

  & {
    color: ${colors.neutral08};
  }
`;

export const Subhead = styled.p`
  ${fonts.paragraph}
  margin: ${spacing(2)} 0 0;

  & {
    color: ${colors.neutral08};
  }

  ${mediaQuery.tabletPortrait`
    padding-right: ${spacing(8)};
  `}
`;

const bigTrapezoidStyles = css`
  content: ' ';
  position: absolute;
  left: 0;
  right: 0;
  height: ${TRAPEZOID_HEIGHT_MOBILE}; // height of 'square' part
  width: 0;
  z-index: -1;

  border-left: ${BANNER_WIDTH_MOBILE} solid ${colors.core01}; // width of banner
  border-bottom: ${BIG_TRIANGLE_HEIGHT_MOBILE} solid transparent; // height of triangle tip

  ${mediaQuery.tabletPortrait`
    height: ${TRAPEZOID_HEIGHT_TABLET};
    border-left: ${BANNER_WIDTH_TABLET} solid ${colors.core01};
    border-bottom: ${BIG_TRIANGLE_HEIGHT_TABLET} solid transparent;
  `}

  ${mediaQuery.tabletLandscape`
    width: ${TRAPEZOID_WIDTH};
    height: 0;
    border-left: none;
    border-bottom: none;
    border-top: ${BANNER_HEIGHT} solid ${colors.core01}; // height of banner
    border-right: ${BIG_TRIANGLE_WIDTH} solid transparent; // width of triange tip
  `}

  @media screen and (min-width: ${LARGE_DESKTOP_BREAKPOINT}) {
    width: ${TRAPEZOID_WIDTH_LARGE};
    border-right: ${BIG_TRIANGLE_WIDTH_LARGE} solid transparent; // width of triange tip
  }
`;

const smallTriangleStyles = css`
  content: ' ';
  position: absolute;
  top: 122px;
  right: 0;
  height: 0;
  width: 0;
  z-index: -2;

  border-top: ${SMALL_TRIANGLE_HEIGHT_MOBILE} solid ${colors.core02};
  border-left: ${SMALL_TRIANGLE_WIDTH_MOBILE} solid transparent;

  ${mediaQuery.tabletPortrait`
    top: 0;
    border-top: ${SMALL_TRIANGLE_HEIGHT_TABLET} solid ${colors.core02};
    border-left: ${SMALL_TRIANGLE_WIDTH_TABLET} solid transparent;
  `}

  ${mediaQuery.tabletLandscape`
    border-top: none;
    border-left: none;
    left: ${TRAPEZOID_WIDTH};
    top: calc(${BANNER_HEIGHT} - ${SMALL_TRIANGLE_WIDTH});
    border-bottom: ${SMALL_TRIANGLE_WIDTH} solid ${colors.core02};
    border-right: ${SMALL_TRIANGLE_HEIGHT} solid transparent;
  `}

  @media screen and (min-width: ${LARGE_DESKTOP_BREAKPOINT}) {
    left: ${TRAPEZOID_WIDTH_LARGE};
  }
`;

export const OrigamiBackground = styled.div`
  position: relative;
  max-width: 1880px;

  height: ${TOTAL_HEIGHT_MOBILE};

  /* takes background to full screen */
  margin: 0 calc(50% - 50vw) ${spacing(4)};
  /* resets the content back to the grid */
  padding: 0 calc(50vw - 50%);

  ${mediaQuery.tabletPortrait`
    height: ${TOTAL_HEIGHT_TABLET};
  `}

  ${mediaQuery.tabletLandscape`
    height: ${BANNER_HEIGHT};
    margin-bottom: ${spacing(8)};
  `}

  @media (min-width: ${LARGE_DESKTOP_BREAKPOINT}) {
    // max margin and padding for max-width
    margin: 0
      ${({ theme }) =>
        `calc((${
          theme.layout.desktop.containerWidthPx ?? 1200
        }px - ${LARGE_DESKTOP_BREAKPOINT}) / 2)`}  ${spacing(8)};
    padding: 0
      ${({ theme }) =>
        `calc((${LARGE_DESKTOP_BREAKPOINT} - ${
          theme.layout.desktop.containerWidthPx ?? 1200
        }px) / 2)`};
  }

  /* Large trapezoid on the top */
  &::before {
    ${bigTrapezoidStyles}
  }

  /* Smaller triangle underneath */
  &::after {
    ${smallTriangleStyles}
  }
`;
